import React, { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, ScrollText, Truck, Clock, Hourglass, Landmark, ClipboardList, Percent, Users } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import SearchableSelect from '../../components/crm/SearchableSelect';
import Tabs from '../../components/ui/Tabs';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const TABS = [
  { key: 'customer', label: 'Customer statement', icon: ScrollText },
  { key: 'vendor', label: 'Vendor statement', icon: Truck },
  { key: 'ar', label: 'Receivables ageing', icon: Clock },
  { key: 'ap', label: 'Payables ageing', icon: Hourglass },
  { key: 'tax', label: 'GST / tax summary', icon: Landmark },
  { key: 'register', label: 'Purchase register', icon: ClipboardList },
  { key: 'tds', label: 'TDS deducted', icon: Percent },
  { key: 'reps', label: 'By salesperson', icon: Users },
];
const INPUT = 'h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3]';
const LABEL = 'block text-[11px] font-semibold text-[#706E6B] mb-1';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monthStart = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
const errMsg = (e, f) => e?.response?.data?.message || f;

async function download(url, params, filename) {
  try {
    const res = await api.get(url, { params, responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (e) {
    alertService.error('Download failed', 'Please try again');
  }
}

function DateRange({ from, to, setFrom, setTo }) {
  return (
    <>
      <div><label className={LABEL} htmlFor="fr-from">From</label><input id="fr-from" type="date" className={INPUT} value={from} onChange={(e) => setFrom(e.target.value)} /></div>
      <div><label className={LABEL} htmlFor="fr-to">To</label><input id="fr-to" type="date" className={INPUT} value={to} onChange={(e) => setTo(e.target.value)} /></div>
    </>
  );
}

function Statement({ kind }) {
  const [parties, setParties] = useState([]);
  const [partyKind, setPartyKind] = useState(kind === 'vendor' ? 'vendor' : 'account');
  const [partyId, setPartyId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPartyId(''); setData(null);
    const url = partyKind === 'vendor' ? '/crm/purchases/vendors' : partyKind === 'account' ? '/crm/accounts' : '/crm/contacts';
    api.get(url, { params: { limit: 500 } })
      .then((res) => {
        const list = res.data.vendors || res.data.accounts || res.data.contacts || res.data || [];
        setParties(list.map((p) => ({ _id: p._id, name: p.name || p.email || 'Unnamed' })));
      })
      .catch(() => setParties([]));
  }, [partyKind]);

  const params = { [partyKind === 'vendor' ? 'vendorId' : partyKind === 'account' ? 'accountId' : 'contactId']: partyId, from: from || undefined, to: to || undefined };
  const load = useCallback(() => {
    if (!partyId) return;
    setLoading(true);
    api.get('/crm/finance-reports/statement', { params })
      .then((res) => setData(res.data))
      .catch((e) => alertService.error('Could not load statement', errMsg(e, '')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, from, to, partyKind]);
  useEffect(() => { load(); }, [load]);

  const isCustomer = kind === 'customer';
  const c = data?.currency;
  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-wrap items-end gap-3">
        {isCustomer && (
          <div>
            <label className={LABEL} htmlFor="st-kind">Customer type</label>
            <select id="st-kind" className={INPUT} value={partyKind} onChange={(e) => setPartyKind(e.target.value)}>
              <option value="account">Account</option><option value="contact">Contact</option>
            </select>
          </div>
        )}
        <div className="w-72">
          <span className={LABEL}>{isCustomer ? 'Customer' : 'Vendor'}</span>
          <SearchableSelect value={partyId} onChange={setPartyId} options={parties} placeholder={isCustomer ? 'Choose customer…' : 'Choose vendor…'} />
        </div>
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <button type="button" disabled={!partyId} onClick={() => download('/crm/finance-reports/statement/pdf', params, `statement-${data?.party?.name || 'party'}.pdf`)}
          className="ml-auto h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap shrink-0"><Download size={14} /> Download PDF</button>
      </div>

      {!partyId ? (
        <p className="text-[13px] text-[#706E6B] text-center py-12 bg-white border border-[#D8DDE6] rounded-lg">Choose a {isCustomer ? 'customer' : 'vendor'} to see their statement of account.</p>
      ) : data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[['Opening balance', data.openingBalance], [isCustomer ? 'Invoiced' : 'Billed', data.totals?.increase], [isCustomer ? 'Received' : 'Paid', data.totals?.decrease], [isCustomer ? 'Balance due' : 'You owe', data.closingBalance]].map(([k, v], i) => (
              <div key={k} className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
                <p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">{k}</p>
                <p className={cn('text-[20px] font-bold tabular-nums mt-1', i === 3 ? 'text-[#0176D3]' : 'text-[#2E2E2E]')}>{formatCurrency(v || 0, c)}</p>
              </div>
            ))}
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
            <table className="w-full text-[13px] sm:min-w-[45rem]">
              <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
                <th className="text-left font-semibold px-4 py-2.5">Date</th><th className="text-left font-semibold px-4 py-2.5">Details</th>
                <th className="text-right font-semibold px-4 py-2.5">{isCustomer ? 'Invoiced' : 'Billed'}</th>
                <th className="text-right font-semibold px-4 py-2.5">{isCustomer ? 'Received' : 'Paid'}</th>
                <th className="text-right font-semibold px-4 py-2.5">Balance</th>
              </tr></thead>
              <tbody>
                <tr className="border-b border-[#F3F2F2] bg-[#F7F9FB] font-semibold"><td className="px-4 py-2.5">{from ? fmtDate(from) : ''}</td><td className="px-4 py-2.5">Opening balance</td><td /><td /><td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(data.openingBalance, c)}</td></tr>
                {data.lines.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#706E6B]">{loading ? 'Loading…' : 'No activity in this period.'}</td></tr>}
                {data.lines.map((l, i) => (
                  <tr key={i} className="border-b border-[#F3F2F2]">
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(l.date)}</td>
                    <td className="px-4 py-2.5">{l.description}{l.reference && <span className="text-[#706E6B]"> · {l.reference}</span>}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{l.increase ? formatCurrency(l.increase, c) : ''}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#2E844A]">{l.decrease ? formatCurrency(l.decrease, c) : ''}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{formatCurrency(l.balance, c)}</td>
                  </tr>
                ))}
                <tr className="bg-[#F7F9FB] font-bold"><td className="px-4 py-2.5" /><td className="px-4 py-2.5">Closing balance</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(data.totals?.increase || 0, c)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(data.totals?.decrease || 0, c)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(data.closingBalance, c)}</td></tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Ageing({ kind }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/crm/finance-reports/${kind === 'ar' ? 'receivables' : 'payables'}-ageing`)
      .then((res) => setData(res.data)).catch((e) => alertService.error('Could not load ageing', errMsg(e, ''))).finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-[13px] text-[#706E6B] py-12 text-center">{loading ? 'Loading…' : ''}</p>;
  const overdue = data.grandTotal - (data.totals.current || 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">{kind === 'ar' ? 'Total receivable' : 'Total payable'}</p><p className="text-[20px] font-bold tabular-nums mt-1">{formatCurrency(data.grandTotal)}</p></div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">Overdue</p><p className="text-[20px] font-bold tabular-nums mt-1 text-[#BA0517]">{formatCurrency(overdue)}</p></div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">Over 90 days</p><p className="text-[20px] font-bold tabular-nums mt-1 text-[#BA0517]">{formatCurrency(data.totals.d90_plus)}</p></div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">{kind === 'ar' ? 'Customers owing' : 'Vendors owed'}</p><p className="text-[20px] font-bold tabular-nums mt-1">{data.rows.length}</p></div>
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-[13px] sm:min-w-[51.25rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
            <th className="text-left font-semibold px-4 py-2.5">{kind === 'ar' ? 'Customer' : 'Vendor'}</th>
            {data.buckets.map((b) => <th key={b.key} className="text-right font-semibold px-4 py-2.5">{b.label}</th>)}
            <th className="text-right font-semibold px-4 py-2.5">Total</th>
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#706E6B]">Nothing outstanding.</td></tr>}
            {data.rows.map((r) => (
              <tr key={`${r.partyId}-${r.currency}`} className="border-b border-[#F3F2F2]">
                <td className="px-4 py-2.5"><span className="font-semibold">{r.name}</span><span className="block text-[11px] text-[#706E6B]">{r.documents} open {kind === 'ar' ? 'invoice' : 'bill'}{r.documents === 1 ? '' : 's'}</span></td>
                {data.buckets.map((b) => (
                  <td key={b.key} className={cn('px-4 py-2.5 text-right tabular-nums', b.key !== 'current' && r.buckets[b.key] > 0 && 'text-[#BA0517]')}>{r.buckets[b.key] ? formatCurrency(r.buckets[b.key], r.currency) : '—'}</td>
                ))}
                <td className="px-4 py-2.5 text-right tabular-nums font-bold">{formatCurrency(r.total, r.currency)}</td>
              </tr>
            ))}
            {data.rows.length > 0 && (
              <tr className="bg-[#F7F9FB] font-bold"><td className="px-4 py-2.5">Total</td>
                {data.buckets.map((b) => <td key={b.key} className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(data.totals[b.key])}</td>)}
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(data.grandTotal)}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#706E6B]">EMI invoices are aged per instalment, by each instalment's own due date.</p>
    </div>
  );
}

function TaxSummary() {
  const currency = useCompanyCurrency();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/crm/finance-reports/tax-summary', { params: { from, to } })
      .then((res) => setData(res.data)).catch((e) => alertService.error('Could not load tax summary', errMsg(e, '')));
  }, [from, to]);
  const c = data?.currency || currency;
  const gst = data && (data.output.cgst || data.output.igst || data.inputCredit.cgst || data.inputCredit.igst);
  const heads = gst ? [['cgst', 'CGST'], ['sgst', 'SGST'], ['igst', 'IGST']] : [];
  if (data?.output.other || data?.inputCredit.other || !gst) heads.push(['other', gst ? 'Other tax' : 'Tax (VAT / sales tax)']);
  const rows = data ? [
    ['Tax on sales (output)', data.output, false],
    ['Tax on reverse-charge purchases', data.reverseCharge, false],
    ['Less: input tax credit', data.inputCredit, true],
  ] : [];
  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-wrap items-end gap-3">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
        {data && <p className="text-[12px] text-[#706E6B] ml-auto">{data.counts.invoices} invoices · {data.counts.bills} purchase bills · {data.currency}</p>}
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">Taxable sales</p><p className="text-[20px] font-bold tabular-nums mt-1">{formatCurrency(data.output.taxable, c)}</p></div>
            <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">Net tax payable</p><p className="text-[20px] font-bold tabular-nums mt-1 text-[#BA0517]">{formatCurrency(data.netPayable, c)}</p></div>
            <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">Credit carried forward</p><p className="text-[20px] font-bold tabular-nums mt-1 text-[#2E844A]">{formatCurrency(data.creditCarriedForward, c)}</p></div>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
            <table className="w-full text-[13px] sm:min-w-[40rem]">
              <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
                <th className="text-left font-semibold px-4 py-2.5" />
                {heads.map(([, l]) => <th key={l} className="text-right font-semibold px-4 py-2.5">{l}</th>)}
                <th className="text-right font-semibold px-4 py-2.5">Total</th>
              </tr></thead>
              <tbody>
                {rows.map(([label, h, less]) => (
                  <tr key={label} className="border-b border-[#F3F2F2]">
                    <td className="px-4 py-2.5">{label}</td>
                    {heads.map(([k]) => <td key={k} className={cn('px-4 py-2.5 text-right tabular-nums', less && 'text-[#2E844A]')}>{less && h[k] ? '−' : ''}{formatCurrency(h[k], c)}</td>)}
                    <td className={cn('px-4 py-2.5 text-right tabular-nums font-semibold', less && 'text-[#2E844A]')}>{less && h.total ? '−' : ''}{formatCurrency(h.total, c)}</td>
                  </tr>
                ))}
                <tr className="bg-[#F7F9FB] font-bold">
                  <td className="px-4 py-2.5">Net payable</td>
                  {heads.map(([k]) => <td key={k} className={cn('px-4 py-2.5 text-right tabular-nums', data.payable[k] < 0 && 'text-[#2E844A]')}>{formatCurrency(data.payable[k], c)}</td>)}
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(data.payable.total, c)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-[#706E6B]">Blocked credit not claimed: <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(data.blockedCredit.total, c)}</strong>. Figures are a working summary for filing (GSTR-3B style); file the return itself on the government portal. A negative head is credit you can set off or carry forward.</p>
        </>
      )}
    </div>
  );
}

function PurchaseRegister() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/crm/finance-reports/purchase-register', { params: { from, to } })
      .then((res) => setData(res.data)).catch((e) => alertService.error('Could not load register', errMsg(e, '')));
  }, [from, to]);
  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-wrap items-end gap-3">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <button type="button" onClick={() => download('/crm/finance-reports/purchase-register', { from, to, format: 'csv' }, `purchase-register-${from}-to-${to}.csv`)}
          className="ml-auto h-9 px-4 rounded border border-[#C9C9C9] bg-white text-[13px] font-semibold flex items-center gap-1.5 whitespace-nowrap shrink-0"><Download size={14} /> Export CSV</button>
      </div>
      {data && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
          <table className="w-full text-[12px] sm:min-w-[62.5rem]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
              {['Date', 'Bill', 'Vendor', 'GSTIN', 'Taxable', 'CGST', 'SGST', 'IGST', 'ITC claimed', 'Blocked', 'Total'].map((h, i) => (
                <th key={h} className={cn('font-semibold px-3 py-2.5', i >= 4 ? 'text-right' : 'text-left')}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-[#706E6B]">No approved bills in this period.</td></tr>}
              {data.rows.map((r) => (
                <tr key={r._id} className={cn('border-b border-[#F3F2F2]', r.status === 'void' && 'opacity-50 line-through')}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.billDate)}</td>
                  <td className="px-3 py-2"><span className="font-semibold">{r.billNumber}</span>{r.vendorBillNumber && <span className="block text-[11px] text-[#706E6B]">#{r.vendorBillNumber}</span>}{r.reverseCharge && <span className="ml-1 text-[10px] font-semibold text-[#92400E]">RCM</span>}</td>
                  <td className="px-3 py-2">{r.vendor}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.vendorGstin || '—'}</td>
                  {['taxable', 'cgst', 'sgst', 'igst', 'itc', 'blocked', 'total'].map((k) => <td key={k} className="px-3 py-2 text-right tabular-nums">{formatCurrency(r[k] || 0, r.currency)}</td>)}
                </tr>
              ))}
              {data.rows.length > 0 && (
                <tr className="bg-[#F7F9FB] font-bold"><td className="px-3 py-2" colSpan={4}>Total (excluding void)</td>
                  {['taxable', 'cgst', 'sgst', 'igst', 'itc', 'blocked', 'total'].map((k) => <td key={k} className="px-3 py-2 text-right tabular-nums">{formatCurrency(data.totals[k])}</td>)}</tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TdsReport() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/crm/finance-reports/tds', { params: { from, to } })
      .then((res) => setData(res.data)).catch((e) => alertService.error('Could not load TDS report', errMsg(e, '')));
  }, [from, to]);
  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-wrap items-end gap-3">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">TDS deducted in period</p><p className="text-[20px] font-bold tabular-nums mt-1">{formatCurrency(data.totalTds)}</p></div>
            <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">TDS payable (books)</p><p className="text-[20px] font-bold tabular-nums mt-1 text-[#BA0517]">{formatCurrency(data.payableBalance)}</p></div>
            <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4"><p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">By section</p><p className="text-[13px] mt-1">{data.sections.length ? data.sections.map((s) => `${s.section}: ${formatCurrency(s.tds)}`).join(' · ') : '—'}</p></div>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
            <table className="w-full text-[13px] sm:min-w-[47.5rem]">
              <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
                {['Date', 'Vendor', 'PAN', 'Section', 'Rate', 'Gross paid', 'TDS', 'Net paid'].map((h, i) => <th key={h} className={cn('font-semibold px-4 py-2.5', i >= 4 ? 'text-right' : 'text-left')}>{h}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#706E6B]">No TDS deducted in this period.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r._id} className="border-b border-[#F3F2F2]">
                    <td className="px-4 py-2.5">{fmtDate(r.date)}</td><td className="px-4 py-2.5">{r.vendor}</td>
                    <td className="px-4 py-2.5 font-mono text-[12px]">{r.pan || <span className="text-[#BA0517] font-sans">missing</span>}</td>
                    <td className="px-4 py-2.5">{r.section}</td><td className="px-4 py-2.5 text-right">{r.rate ? `${r.rate}%` : '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(r.grossPaid, r.currency)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{formatCurrency(r.tds, r.currency)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(r.netPaid, r.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-[#706E6B]">Working data for depositing TDS and filing the quarterly return (26Q). Vendors without a PAN are flagged — a higher rate applies without one.</p>
        </>
      )}
    </div>
  );
}

// Invoiced, collected and owed per salesperson, with deals won. Invoices
// without a salesperson count for the deal's or customer's owner.
function BySalesperson() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState('');
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get('/crm/finance-reports/by-salesperson', { params: { from: from || undefined, to: to || undefined } })
      .then((r) => setD(r.data)).catch((e) => alertService.error('Could not load', errMsg(e, 'Please try again')));
  }, [from, to]);
  const money = (v) => formatCurrency(v || 0, d?.currency);
  const csv = () => {
    const rows = [['Salesperson', 'Invoices', 'Invoiced (excl. tax)', 'Collected', 'Outstanding now', 'Overdue now', 'Deals won', 'Won value'], ...d.rows.map((r) => [r.name, r.invoices, r.invoiced, r.collected, r.outstanding, r.overdue, r.dealsWon, r.wonValue])];
    const href = URL.createObjectURL(new Blob([rows.map((r) => r.map((c) => (/[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c)).join(',')).join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = href; a.download = 'sales-by-salesperson.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
        {d && <button type="button" onClick={csv} className="h-9 px-3 rounded border border-[#C9C9C9] text-[13px] font-semibold flex items-center gap-1 bg-white whitespace-nowrap shrink-0"><Download size={14} />CSV</button>}
      </div>
      {d && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
          <table className="w-full text-[13px] sm:min-w-[47.5rem]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>
              <th className="px-3 py-2 text-left">Salesperson</th><th className="px-3 py-2 text-right">Invoices</th><th className="px-3 py-2 text-right">Invoiced (excl. tax)</th>
              <th className="px-3 py-2 text-right">Collected</th><th className="px-3 py-2 text-right">Outstanding now</th><th className="px-3 py-2 text-right">Overdue now</th><th className="px-3 py-2 text-right">Deals won</th>
            </tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.userId || 'none'} className="border-t border-[#F3F2F2]">
                  <td className={cn('px-3 py-2 font-semibold', !r.userId && 'text-[#706E6B] italic font-normal')}>{r.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.invoices}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.invoiced)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.collected)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.outstanding)}</td>
                  <td className={cn('px-3 py-2 text-right tabular-nums', r.overdue > 0 && 'text-[#BA0517] font-semibold')}>{money(r.overdue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.dealsWon}{r.wonValue ? <span className="block text-[11px] text-[#706E6B]">{money(r.wonValue)}</span> : null}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#D8DDE6] font-semibold">
                <td className="px-3 py-2">Total</td><td className="px-3 py-2 text-right">{d.totals.invoices}</td><td className="px-3 py-2 text-right tabular-nums">{money(d.totals.invoiced)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(d.totals.collected)}</td><td className="px-3 py-2 text-right tabular-nums">{money(d.totals.outstanding)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(d.totals.overdue)}</td><td className="px-3 py-2 text-right">{d.totals.dealsWon}</td>
              </tr>
            </tbody>
          </table>
          {!d.rows.length && <p className="text-[13px] text-[#706E6B] text-center py-6">No invoices, payments or wins in this period.</p>}
        </div>
      )}
      <p className="text-[12px] text-[#706E6B]">Invoiced and collected are for the dates chosen; outstanding and overdue are as of today. Set the salesperson on an invoice to credit someone other than the deal or customer owner.</p>
    </div>
  );
}

export default function FinanceReportsPage() {
  const [tab, setTab] = useState('customer');
  const [refresh, setRefresh] = useState(0);
  return (
    <div className={PAGE}>
      <PageHeader title="Ledgers & Reports" subtitle="Statements of account, what's owed and overdue, and tax due — all read straight from your books." actions={<>
        <button type="button" onClick={() => setRefresh((n) => n + 1)} aria-label="Refresh" className="h-9 w-9 flex items-center justify-center rounded border border-[#D8DDE6] bg-white"><RefreshCw size={15} /></button>
      </>} />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      <div key={`${tab}-${refresh}`}>
        {tab === 'customer' && <Statement kind="customer" />}
        {tab === 'vendor' && <Statement kind="vendor" />}
        {tab === 'ar' && <Ageing kind="ar" />}
        {tab === 'ap' && <Ageing kind="ap" />}
        {tab === 'tax' && <TaxSummary />}
        {tab === 'register' && <PurchaseRegister />}
        {tab === 'tds' && <TdsReport />}
        {tab === 'reps' && <BySalesperson />}
      </div>
    </div>
  );
}
